import { Tooltip } from "antd";
import {
  WalletOutlined, ThunderboltOutlined, FileProtectOutlined, TeamOutlined,
  CheckSquareOutlined, FileTextOutlined, LoginOutlined,
} from "@ant-design/icons";
import { inr, num, dateTime, relative, StatTile, UsageBar } from "./shared";

/**
 * The account-side stat cards on Overview.
 *
 * The existing Overview answers "what is in this portfolio". These answer "what
 * is the state of the account behind it" — what they have left to spend, how
 * many people are on it, what is still open. Clicking one opens the tab that
 * explains it, so a number is never a dead end.
 */
const AccountSummaryCards = ({ summary, loading, onOpenTab }) => {
  if (loading) {
    return (
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(215px,1fr))", gap: 16, marginBottom: 24 }}>
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={i} style={{
            height: 96, borderRadius: 14, background: "#fff",
            border: "1px solid rgba(0,0,0,0.06)", boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
          }} />
        ))}
      </div>
    );
  }

  if (!summary) return null;

  const credits = summary.ai_credits || {};

  const cards = [
    {
      tab: "wallet",
      node: <StatTile icon={<WalletOutlined />} label="Wallet balance" value={inr(summary.wallet_balance)} color="#6366f1" bg="#eef2ff" />,
    },
    {
      tab: "ai-credits",
      node: (
        <StatTile icon={<ThunderboltOutlined />} label="AI credits" value={num(credits.balance)} color="#7c3aed" bg="#f5f3ff">
          <UsageBar used={credits.used} total={credits.purchased} color="#7c3aed" />
        </StatTile>
      ),
    },
    {
      tab: "tm-breakdown",
      node: (
        /* Subscribed and manual add up to the headline; monitoring does not —
           it is how many of those are actively watched, so it sits on its own
           line rather than being summed into a total that would double-count. */
        <StatTile
          icon={<FileProtectOutlined />} label="Trademarks"
          value={num((Number(summary.total_tm_subscribed) || 0) + (Number(summary.total_tm_manual) || 0))}
          sub={(
            <>
              Subscribed {num(summary.total_tm_subscribed)} · Manual {num(summary.total_tm_manual)}
              <br />
              <span style={{ color: "#d97706", fontWeight: 600 }}>
                {num(summary.total_tm_monitoring)} in monitoring
              </span>
            </>
          )}
          color="#0d9488" bg="#f0fdfa"
        />
      ),
    },
    {
      tab: "team",
      node: <StatTile icon={<TeamOutlined />} label="Team members" value={num(summary.team_members)} color="#2563eb" bg="#eff6ff" />,
    },
    {
      tab: "tasks",
      node: <StatTile icon={<CheckSquareOutlined />} label="Open tasks" value={num(summary.open_tasks)} color="#ea580c" bg="#fff7ed" />,
    },
    {
      tab: "notice-replies",
      node: (
        <StatTile
          icon={<FileTextOutlined />} label="Notice replies" value={num(summary.notice_replies)}
          sub={`${num(summary.notice_replies_generated)} generated`}
          color="#db2777" bg="#fdf2f8"
        />
      ),
    },
    {
      tab: "logins",
      node: (
        /* Relative up front — "2h ago" is what tells you whether this account is
           alive. The exact timestamp is one hover away for when it matters. */
        <Tooltip title={summary.last_login ? dateTime(summary.last_login) : "No login recorded"}>
          <div style={{ height: "100%" }}>
            <StatTile icon={<LoginOutlined />} label="Last login" value={relative(summary.last_login)} color="#0891b2" bg="#ecfeff" />
          </div>
        </Tooltip>
      ),
    },
  ];

  return (
    <div style={{ marginBottom: 24 }}>
      {/* Labelled, because Overview now carries two rows of numbers and they
          answer different questions — this row is the account, the row below is
          the portfolio. */}
      <div style={{
        fontSize: 11.5, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase",
        letterSpacing: 0.6, marginBottom: 10,
      }}>
        Account
      </div>
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(215px,1fr))",
        gap: 16, alignItems: "stretch",
      }}>
        {cards.map((c) => (
          <div
            key={c.tab}
            onClick={() => onOpenTab?.(c.tab)}
            style={{ cursor: "pointer" }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpenTab?.(c.tab); } }}
          >
            {c.node}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AccountSummaryCards;
